import $ from 'jquery';
import _ from 'underscore';
import * as Sentry from '@sentry/browser';
import { track } from 'util/analytics';
import { $body, $window, jqGetElemById, isMobile, setScrollPosition } from 'util/global';
import { crop } from 'util/ui';
import {
  CLASS_NAME_ACTIVE,
  CLASS_NAME_DISABLED,
  EVT_SUBSCRIPTION_SETTINGS_TRIGGER_CLICKED,
} from 'util/constants';
import { showStandardErrorModal, errorModalHandler } from 'app/error_modal';

/** @namespace */
var Paid = {};

var mxpTrackTimeout = function (stepName) {
  track('payment timeout error', {
    step: stepName,
  });
};

Paid.giftSubscribeOptIn = function ($checkbox) {
  $checkbox.on('change', e => {
    const checkbox = e.target;

    if (checkbox.disabled) {
      return;
    }

    const $checkbox = $(checkbox);
    const url = $checkbox.data('url');

    checkbox.disabled = true;

    $.post(url)
      .done(response => {
        if (response.success === true) {
          $checkbox.data('url', response.new_url);
        } else {
          showStandardErrorModal();
        }
      })
      .fail(() => {
        $checkbox.prop('checked', !$checkbox.prop('checked'));
        showStandardErrorModal();
      })
      .always(() => {
        checkbox.disabled = false;
      });
  });
};

/**
 * Toggle between courses and practice in courses paywall section
 * @param {JQuery} $container
 */
Paid.paywallCourses = function ($container) {
  var $toggleBtns = $container.find('.btn-group .btn');
  var activeClass = 'active';

  $toggleBtns.on('click', function () {
    var $btn = $(this);
    if (!$btn.hasClass(activeClass)) {
      $toggleBtns.toggleClass(activeClass);
      $container.toggleClass('practice');
      track('ui', {
        element_id: 'clicked_paywall_courses_tab',
        tab_name: $btn.data('ax-tab'),
        action: 'click',
      });
    }
  });
};

/**
 * Scroll to the premium feature list by default
 * @param {JQuery} $container
 */
Paid.setFeaturesScroll = $container => {
  $container.scrollLeft($container[0].scrollWidth);
};

// checkout
// --------------------------------------------------

/** @namespace */
Paid.Checkout = {};

let $prestatusModal = null;

/**
 * Handle the checkout response
 * @param {JQuery} $element - the modal container
 */
Paid.Checkout.response = function ($element) {
  const data = $element.data();
  const intervalLimit = 60; // quit after 60s
  let nextUrl = window.location.href;
  let intervalCount = 0;
  let interval;

  const $successBtn = jqGetElemById('paid-status-success-btn');

  const handleSuccess = () => {
    if (data.showAppleWarning) {
      jqGetElemById('existing-apple-subscription-warning').modal();
    }

    $element.addClass('loaded success');
    nextUrl = $successBtn.prop('href');
  };

  if (data.success && data.displaySuccess) {
    handleSuccess();
  }
  // successfully created a subscription object
  else if (data.success) {
    // start polling
    interval = setInterval(function () {
      // check the status
      $.get(data.url, { s: data.subscription })
        .then(function (result) {
          if (result === 'active') {
            handleSuccess();
            clearInterval(interval);
          }
        })
        .fail(response => {
          clearInterval(interval);
          errorModalHandler(response);
        });

      // bump the count
      intervalCount++;
      if (intervalCount >= intervalLimit) {
        $element.addClass('loaded error');
        clearInterval(interval);
        Sentry.captureMessage('Paid.Checkout.response: timeout error');
        mxpTrackTimeout('Checkout subscription status');
      }
    }, 1000);

    // error creating subscription
  } else {
    $element.addClass('loaded error');
  }

  // hide active modal
  if ($prestatusModal !== null) {
    $prestatusModal.modal('hide');
  }

  // show the modal
  $element.modal();
  $element.on('hide', () => {
    $body.addClass('disabled');
    window.location.href = nextUrl;
  });
};

// Cancel subscription survey
// --------------------------------------------------

/**
 * Instead of canceling the plan right away, show a survey modal first and cancel once the survey
 * is successfully submitted. Events fire on the cancellation link.
 * Available events: survey.complete, survey.beforeshown
 * @param  {Event}
 * @example
 *     $cancelLink.on('click', handleShowSurvey);
 *     $window.on('survey.complete', actuallyCancelTheSubscription);
 */
var handleShowSurvey = (function () {
  var $modal = null;
  var submitted = false;
  var errorListTmpl = _.template('<ul class="errorlist"><%= list %></ul>');
  var errorItemTmpl = _.template('<li><%- message %></li>');
  var surveyEvent = function ($source, name) {
    return $window.trigger($.Event(`survey.${name}`));
  };

  return function (e) {
    e.preventDefault();

    const $source = $(this);

    if (submitted) {
      surveyEvent($source, 'complete');
      return;
    }

    if ($modal === null) {
      $modal = jqGetElemById('paid-cancel-survey');

      if (!$modal.length) {
        showStandardErrorModal();
        return;
      }

      $modal.on('submit', function (e) {
        e.preventDefault();

        $.post($modal.attr('action'), $modal.serialize())
          .fail(errorModalHandler)
          .then(function (response) {
            if (response.success) {
              submitted = true;
              $modal.modal('hide');
              surveyEvent($source, 'complete');
            } else {
              setScrollPosition(0, {
                $container: $modal.parent(),
              });
              const errorMsgs = [];
              for (var error in response.errors) {
                errorMsgs.push(response.errors[error][0]);
              }

              var errorList = errorMsgs
                .map(function (message) {
                  return errorItemTmpl({
                    message: message,
                  });
                })
                .join('');

              _.defer(function () {
                $modal.find('.errorlist').remove();
                $modal.find('.modal-body').prepend(
                  errorListTmpl({
                    list: errorList,
                  }),
                );
              });
            }
          });
      });
    }

    surveyEvent($source, 'beforeshown');
    $modal.modal();
  };
})();

// Payment methods form
// --------------------------------------------------

/** @namespace */
Paid.Payment = {};

var $cancelPaymentModal = null,
  activeCancelData = null;

var cancelPayment = function (data) {
  $.get(data.url, data)
    .then(() => {
      // response is either 'canceled' or 'error'
      // reload for both cases
      $body.addClass('disabled');
      window.location.reload();
    })
    .fail(errorModalHandler);
};

/**
 * Modal for canceling subscription
 */
Paid.Payment.cancelModal = function ($modal) {
  $cancelPaymentModal = $modal.clone(true);

  $cancelPaymentModal.modal({
    show: false,
  });

  $cancelPaymentModal.find('#paid-cancel').on('click', e => {
    const $upgradeModal = jqGetElemById('payment-switch-plan-modal').clone(true);
    const cancelButton = e.currentTarget;

    if ($upgradeModal.length) {
      $cancelPaymentModal.modal('hide');
      $upgradeModal.find('.cancel-link').on('click', () => {
        handleShowSurvey.call(cancelButton, e);
      });
      $upgradeModal.addClass('canceling');
      $upgradeModal.modal();
    } else {
      $cancelPaymentModal.modal('hide');
      handleShowSurvey.call(cancelButton, e);
    }
  });

  $window.on('survey.complete', () => {
    jqGetElemById('acct-settings-subscription').find('.cancel a').addClass('disabled');
    if (activeCancelData.isPaytm) {
      const $cancelConfirmModal = jqGetElemById(`cancel-confirm-${activeCancelData.id}`);
      $cancelConfirmModal.modal('show');
      $cancelConfirmModal.find('#confirm-btn').on('click', () => {
        cancelPayment(activeCancelData);
      });
    } else if (activeCancelData !== null) {
      cancelPayment(activeCancelData);
    }
  });
};

/**
 * Renew button
 * @param {JQuery} $button
 */
Paid.Payment.Renew = $button => {
  $button.on('click', e => {
    e.preventDefault();
    $button.addClass(CLASS_NAME_DISABLED);
    $body.addClass(CLASS_NAME_DISABLED);
    $.get($(e.currentTarget).attr('href'))
      .then(() => window.location.reload())
      .fail(errorModalHandler)
      .fail(() => $body.removeClass(CLASS_NAME_DISABLED));
  });
};

/**
 * Opens Stripe checkout form for card updating
 * @param {JQuery} $button
 */
Paid.Payment.UpdateCard = $button => {
  $button.on('click', e => {
    if (!$button.hasClass(CLASS_NAME_DISABLED)) {
      const $cardInfo = jqGetElemById('card-info');
      const $spinner = $cardInfo.find('.spinner');

      const { key, image, email, name, url, buttonLabel } = $(e.target).data();

      const stripeData = {
        key,
        image,
        name,
        url,
        email: email || null,
        allowRememberMe: false,
      };

      const stripeConfig = Object.assign(
        {
          panelLabel: buttonLabel,
          token(token) {
            const visibleClass = 'visible';
            $button.addClass(CLASS_NAME_DISABLED);
            $spinner.addClass(visibleClass);
            $.post(url, { stripeToken: token.id })
              .then(() => {
                ['last4', 'exp_month', 'exp_year'].forEach(property => {
                  $cardInfo.find(`.${property}`).text(token.card[property]);
                });
                $button.removeClass(CLASS_NAME_DISABLED);
                $spinner.removeClass(visibleClass);
              })
              .fail(errorModalHandler);
          },
          closed() {
            $button.removeClass(CLASS_NAME_DISABLED);
          },
        },
        stripeData,
      );

      $button.addClass(CLASS_NAME_DISABLED);
      StripeCheckout.configure(stripeConfig).open(stripeData);
    }
  });
};

/**
 * Opens subscription settings modal.
 * @param {JQuery} $trigger
 */
Paid.Payment.ShowSubscriptionSettings = $trigger => {
  $trigger.on('click', e => {
    e.preventDefault();
    window.dispatchEvent(new Event(EVT_SUBSCRIPTION_SETTINGS_TRIGGER_CLICKED));
    track('ui', {
      element_id: 'clicked_subscription_settings',
      action: 'click',
    });
  });
};

/**
 * Controller for the subscription settings modal component wrapper.
 * @param {JQuery} $modal
 */
Paid.Payment.SubscriptionSettingsModal = $modal => {
  $window.on('SubscriptionSettingsModal.error', (e, data) => {
    showStandardErrorModal();
  });
};

/**
 * Controller.
 * Shows confirmation modal when switching to yearly plan.
 * @param {JQuery} $trigger
 */
Paid.switchPlanModal = $trigger => {
  $trigger.on('click', () => {
    jqGetElemById('payment-switch-plan-modal').clone(true).modal();
  });
};

/**
 * Handle redeeming a Premium perk (e.g. Nautilus subscription)
 * @param {JQuery} $form
 */
Paid.redeemPremiumPerk = $form => {
  $form.on('submit', e => {
    e.preventDefault();
    $form.find('button').prop('disabled', true);
    const formData = $form.data();
    const params = {
      perk_type: formData.perkType,
    };
    $.post($form.prop('action'), params)
      .fail(showStandardErrorModal)
      .then(response => {
        if (response.success === true) {
          if (formData.nextUrl) {
            window.location.href = formData.nextUrl;
          } else {
            $form.parents('.modal').modal('hide');
            jqGetElemById('perk-modal-trigger').addClass(CLASS_NAME_ACTIVE);
          }
        } else {
          showStandardErrorModal();
        }
      });
  });
};

/**
 * Controller for sales card B2 buttons
 * @param {JQuery} $btn
 */
Paid.salesCardTrigger = function ($btn) {
  import('sales_card.less');
  if (isMobile) {
    import('mobile/sales_card.less');
  }
};

/**
 * Adds twinkling stars to the container.
 * @param {JQuery} $container
 */
Paid.twinklingStars = function ($container) {
  // remove old stars in case of re-applying this method to some container
  $container.find('.premium-stars').remove();

  var data = $container.data();
  if (!data) {
    return;
  }

  var containerOffset = $container.offset();
  var containerWidth = $container.outerWidth();
  var containerHeight = $container.outerHeight();
  var stars = [];
  var STAR_COUNT = data['starCount'];
  var PADDING = 10; // padding between excluded elements and stars, px
  var DURATION = 4000; // duration of one animation cycle, ms
  var COMMON_DELAY = 5000; // stars start twinkling after this time, ms

  var importantElements = [];
  $container.find('.no-stars').each(function () {
    var $elem = $(this);
    var offset = $elem.offset();
    // calculate coordinates of the elements inside the container
    importantElements.push({
      left: offset.left - containerOffset.left - PADDING,
      right: offset.left + $elem.outerWidth() - containerOffset.left + PADDING,
      top: offset.top - containerOffset.top - PADDING,
      bottom: offset.top + $elem.outerHeight() - containerOffset.top + PADDING,
    });
  });

  /**
   * Determines whether a star in this position would interfere with important elements.
   * @param {{left: number, top: number}} position
   * @returns {boolean}
   */
  var isBadPosition = function (position) {
    // calculate coordinates inside the container, assuming that star size is 3x3 px
    var starX = (position.left / 100) * containerWidth + 1.5;
    var starY = (position.top / 100) * containerHeight + 1.5;

    for (var i = 0; i < importantElements.length; i++) {
      var elem = importantElements[i];
      if (starX < elem.left || starX > elem.right || starY < elem.top || starY > elem.bottom) {
        continue;
      }
      return true;
    }

    return false;
  };

  var starsContainer = document.createElement('span');
  starsContainer.className = 'premium-stars';

  for (var i = 0; i < STAR_COUNT; i++) {
    // try to generate a good position for the star, limit number of tries just in case
    var tries = 0;
    do {
      var position = {
        left: 0.5 + Math.random() * 99,
        top: 2 + Math.random() * 96,
      };
      tries++;
    } while (isBadPosition(position) && tries < 50);

    stars[i] = document.createElement('span');
    stars[i].className = 'type-' + ~~(Math.random() * 3 + 1);
    stars[i].style.left = position.left + '%';
    stars[i].style.top = position.top + '%';

    starsContainer.appendChild(stars[i]);
  }

  $container.append(starsContainer);

  if (!data.static || data.static !== true) {
    _.defer(function () {
      for (var i = 0; i < STAR_COUNT; i++) {
        var delay = ~~(Math.random() * DURATION) + COMMON_DELAY;
        stars[i].style.webkitAnimationDuration = stars[i].style.animationDuration = DURATION + 'ms';
        stars[i].style.webkitAnimationDelay = stars[i].style.animationDelay = delay + 'ms';
      }
    });
  }
};

/**
 * Recurring gift card.
 * @param {JQuery} $container
 */
Paid.giftPurchaseList = function ($container) {
  let templates = {
    giftSummaryModal: _.template(jqGetElemById('gift-summary-modal-template').text()),
    turnOffRecurringGiftModal: _.template(
      jqGetElemById('turn-off-recurring-gift-modal-template').text(),
    ),
  };

  $container.find('.recurring-gift').each((i, elem) => {
    const $gift = $(elem);
    const data = $gift.data();

    const updateGiftParams = state => {
      const dataKey = 'subscription-state';
      $gift.attr(`data-${dataKey}`, state);
      $gift.data(dataKey, state);
    };

    $gift.find('.show-gift-summary-modal').on('click', () => {
      const $giftSummaryModal = $(templates.giftSummaryModal(data));
      const $autoRenewNote = $giftSummaryModal.find('.auto-renew-note');

      $('.gift-summary-modal').remove(); // remove old modals if any
      $body.append($giftSummaryModal);
      $giftSummaryModal.modal();

      $giftSummaryModal.find('.turn-on-auto-renew').on('click', e => {
        e.preventDefault();

        const $link = $(e.currentTarget);
        if ($link.hasClass(CLASS_NAME_DISABLED)) {
          return;
        }

        const turnOnAutoRenewURL = $link.attr('href');

        $link.addClass(CLASS_NAME_DISABLED);

        $.post(turnOnAutoRenewURL)
          .fail(showStandardErrorModal)
          .then(response => {
            if (response.state === 'active') {
              $autoRenewNote.removeClass('onetime').addClass('recurring');
              $link.removeClass(CLASS_NAME_DISABLED);
              updateGiftParams(response.state);
            } else {
              showStandardErrorModal();
            }
          });
      });

      $giftSummaryModal.find('.turn-off-auto-renew').on('click', e => {
        e.preventDefault();

        const $link = $(e.currentTarget);
        if ($link.hasClass(CLASS_NAME_DISABLED)) {
          return;
        }

        const turnOffAutoRenewURL = $link.attr('href');
        const $turnOffRecurringGiftModal = $(templates.turnOffRecurringGiftModal(data));

        $giftSummaryModal.modal('hide');

        $('.turn-off-recurring-gift-modal').remove(); // remove old modals if any
        $body.append($giftSummaryModal);
        $turnOffRecurringGiftModal.modal();

        $turnOffRecurringGiftModal.on('hide', () => {
          $giftSummaryModal.modal('show');
          _.defer(crop);
        });

        $turnOffRecurringGiftModal.find('.turn-off').on('click', () => {
          $turnOffRecurringGiftModal.find('button').addClass(CLASS_NAME_DISABLED);
          $.post(turnOffAutoRenewURL)
            .fail(showStandardErrorModal)
            .then(response => {
              if (response.state === 'recurring_stopped' || response.state === 'non_renewing') {
                $autoRenewNote.removeClass('recurring').addClass('onetime');
                $turnOffRecurringGiftModal.modal('hide');
                updateGiftParams(response.state);
              } else {
                showStandardErrorModal();
              }
            });
        });
      });
    });
  });
};

Paid.refundForm = $form => {
  const NOT_WHAT_I_EXPECTED_CLASS = 'not-what-i-expected';
  const ANOTHER_REASON_CLASS = 'another-reason';
  const { minChars, maxChars } = $form.data();

  // Reasons select menu
  const $reasons = $form.find('[name="refund_request_choice"]');

  // Get each TextAreas from sections 2 & 3
  const $textareas = $form.find('textarea');

  // Submit Button
  const $submit = $form.find('button');

  const isTextValid = textareaName => {
    const textarea = $textareas.filter(`[name="${textareaName}"]`)[0];
    const textLength = textarea.value.length;
    return textLength >= minChars && textLength <= maxChars;
  };

  const toggleSubmit = () => {
    let disableSubmitButton = false;

    // Refund choice must be selected
    if ($reasons.val().length === 0) {
      disableSubmitButton = true;
    }

    // validate all visible textareas
    $textareas.filter(':visible').each((index, textarea) => {
      if (!isTextValid(textarea.name)) {
        disableSubmitButton = true;
      }
    });

    $submit.prop('disabled', disableSubmitButton);
  };

  const toggleSecondStep = () => {
    const reason = $reasons.val();
    if (reason === '') {
      $form.removeClass(`${NOT_WHAT_I_EXPECTED_CLASS} ${ANOTHER_REASON_CLASS}`);
    } else {
      // show one textarea if user selects "This isn't what I was expecting",
      // show another textarea otherwise
      const notWhatIExpectedSelected = reason === '5';
      $form.toggleClass(NOT_WHAT_I_EXPECTED_CLASS, notWhatIExpectedSelected);
      $form.toggleClass(ANOTHER_REASON_CLASS, !notWhatIExpectedSelected);
    }
  };

  $textareas.on('change keyup', toggleSubmit);
  $reasons.on('change', () => {
    toggleSecondStep();
    toggleSubmit();
  });
};

module.exports = Paid;
